import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { ChangeCell } from '@/components/ChangeCell'
import type {
  AuditEntry,
  CrewDayMoveResult,
  JobSummary,
  SiteCheckIn,
  SubmissionDetail,
  TimeEntryDetail,
  WorkTypeOption,
} from '@/api/types'

/**
 * "07:30:00" from the API rendered as "7:30 AM".
 *
 * SQL Server TIME serialises with seconds; nobody records a start time to the
 * second, so they are dropped rather than shown as noise.
 */
function clockLabel(t: string | null): string {
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return t
  const suffix = h < 12 ? 'AM' : 'PM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`
}

/**
 * One crew day, with correction.
 *
 * Under Phase 1's implicit approval, entering time IS approving it — there is
 * no second pair of eyes before it counts. Corrections are therefore routine,
 * and without this screen the only way to fix a typo is the database.
 *
 * Once a line has posted to Sage the API refuses to change it. That is
 * deliberate: the portal has no authority to reverse a posted Time Track row,
 * and editing the portal copy would silently desynchronise the two.
 */

/** Per diem's cost code. A supervisor may remove these lines but never price them. */
const PER_DIEM_COST_CODE = '860000000'

export function SubmissionDetailPage() {
  const { submissionId = '' } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  // Below Office. Held to a note on every change, and kept away from per
  // diem amounts -- the server enforces both; this only stops the screen
  // offering what will be refused.
  const isField = user?.role === 'Supervisor' || user?.role === 'Technician'

  const [detail, setDetail] = useState<SubmissionDetail | null>(null)

  /* --- Was this day recorded from the site? -------------------------------
   *
   * Shown HERE rather than on a screen of its own, because a reading means
   * something only next to the day it belongs to. A list of coordinates
   * answers no question anybody has; "was this day recorded from that job"
   * is the question.
   *
   * Loaded separately and allowed to fail quietly: whether somebody tapped
   * "on site" is context, and the crew day has to render without it.
   */
  const [checkIns, setCheckIns] = useState<SiteCheckIn[]>([])
  const [audit, setAudit] = useState<AuditEntry[]>([])
  // All work types, including office-only ones. Reclassifying a line to
  // Prevailing Regular before export is exactly what this screen is for.
  const [workTypes, setWorkTypes] = useState<WorkTypeOption[]>([])
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState({ value: '', workTypeId: 0 })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const d = await api.submission(submissionId)
      setDetail(d)
      const [a, wt] = await Promise.all([
        api.submissionAudit(submissionId),
        api.workTypes(false),
      ])
      setAudit(a)
      setWorkTypes(wt)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load')
    }
  }, [submissionId])

  useEffect(() => {
    void load()
  }, [load])

  const posted = detail?.status === 'PostedToSage'

  function startEdit(e: TimeEntryDetail) {
    setEditing(e.time_entry_id)
    // Match on the code pairing, since the line stores codes rather than a
    // label — a line corrected before the work type list changed still lands
    // on the right entry.
    const wt = workTypes.find(
      (w) => w.cost_code === e.cost_code && w.earnings_code === e.earnings_code,
    )
    setDraft({
      value: String(e.entry_type === 'Hours' ? (e.hours_worked ?? '') : (e.amount ?? '')),
      workTypeId: wt?.work_type_id ?? 0,
    })
    setError(null)
  }

  /** The note rule, checked before the request rather than after a refusal. */
  function needsNote(): boolean {
    if (isField && !reason.trim()) {
      setError('Add a reason first (the box above the lines) — even just "typo".')
      return true
    }
    return false
  }

  async function save(e: TimeEntryDetail) {
    if (needsNote()) return
    setBusy(true)
    setError(null)
    try {
      const wt = workTypes.find((w) => w.work_type_id === draft.workTypeId)
      const type = wt?.entry_type ?? e.entry_type
      const num = Number(draft.value)
      const updated = await api.correctEntry(submissionId, e.time_entry_id, {
        work_type_id: draft.workTypeId || null,
        hours_worked: type === 'Hours' ? num : null,
        amount: type === 'Amount' ? num : null,
        reason: reason || null,
      })
      setDetail(updated)
      setAudit(await api.submissionAudit(submissionId))
      setEditing(null)
      setNotice('Correction saved and recorded in the audit trail.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the correction')
    } finally {
      setBusy(false)
    }
  }

  async function remove(e: TimeEntryDetail) {
    if (needsNote()) return
    if (!confirm(`Remove ${e.display_name}'s ${e.earnings_description ?? e.earnings_code} line?`))
      return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.deleteEntry(submissionId, e.time_entry_id, reason || undefined)
      setDetail(updated)
      setAudit(await api.submissionAudit(submissionId))
      setNotice('Line removed and recorded in the audit trail.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove the line')
    } finally {
      setBusy(false)
    }
  }

  /* --- Moving the whole day ----------------------------------------------
   *
   * For a day recorded on the wrong job or date. Two steps on purpose: CHECK
   * asks the server what would happen -- per diem re-priced for the new job,
   * people already recorded elsewhere that day, work types that no longer
   * suit the job -- and only MOVE changes anything. Hours, per diem and the
   * site reading go together, so the day is never left half in each place.
   */
  const [moveOpen, setMoveOpen] = useState(false)
  const [moveJob, setMoveJob] = useState('')
  const [moveDate, setMoveDate] = useState('')
  const [jobs, setJobs] = useState<JobSummary[]>([])
  const [preview, setPreview] = useState<CrewDayMoveResult | null>(null)

  function openMove() {
    if (!detail) return
    setMoveOpen(true)
    setMoveJob(detail.job_no)
    setMoveDate(detail.date_worked)
    setPreview(null)
    if (jobs.length === 0) {
      void api.jobs().then(setJobs).catch(() => setError('Could not load the job list.'))
    }
  }

  async function runMove(dryRun: boolean) {
    if (!detail) return
    if (needsNote()) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.moveCrewDay(submissionId, {
        job_no: moveJob !== detail.job_no ? moveJob : null,
        date_worked: moveDate !== detail.date_worked ? moveDate : null,
        reason: reason.trim() || null,
        dry_run: dryRun,
      })
      if (dryRun) {
        setPreview(res)
        return
      }
      if (res.detail) setDetail(res.detail)
      setAudit(await api.submissionAudit(submissionId))
      setMoveOpen(false)
      setPreview(null)
      setNotice(
        `Moved to ${res.to_job_no} on ${res.to_date}.` +
          (res.needs_review ? ' Flagged for the office to check the work types.' : ''),
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move this day')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (!detail) return
    let cancelled = false
    void (async () => {
      try {
        const rows = await api.checkIns({
          jobNo: detail.job_no,
          dateFrom: detail.date_worked,
          dateTo: detail.date_worked,
        })
        if (!cancelled) setCheckIns(rows)
      } catch {
        if (!cancelled) setCheckIns([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [detail?.job_no, detail?.date_worked])

  if (!detail) {
    return (
      <div className="card">
        {error ? <div className="alert alert-error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    )
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>
            <span className="mono">{detail.job_no}</span> · {detail.date_worked}
          </h2>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
        <p className="muted">
          {detail.job_description} · {detail.crew_count} crew · {detail.line_count} lines ·{' '}
          <strong>{Number(detail.total_hours).toFixed(2)}</strong> hours · entered by{' '}
          {detail.submitted_by}
        </p>
        {detail.approval_policy === 'ImplicitOnSubmit' && (
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            Approved on entry — the supervisor recording this day also approved it.
          </p>
        )}
        {/* WHERE IT WAS RECORDED FROM.

            The capture time is always shown, and that is the point: "on site"
            must never be able to hide "tapped at 21:32". Accuracy is shown for
            the same reason -- a 2 km fix places nobody, and looks identical to
            a good one once it is a pair of numbers.

            Nothing here says "verified". Browser geolocation is spoofable and
            the wording should not claim more than the reading supports. */}
        {checkIns.map((c) => (
          <div
            key={c.check_in_id}
            className={
              c.verdict === 'Away from site' ? 'alert alert-warn' : 'alert alert-ok'
            }
            style={{ fontSize: '0.8125rem' }}
          >
            <strong>{c.display_name}</strong> recorded on site at{' '}
            {new Date(c.captured_utc).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
            {c.accuracy_meters != null && (
              <> · accurate to about {Math.round(c.accuracy_meters)} m</>
            )}
            {c.distance_meters != null && (
              <> · {Math.round(c.distance_meters)} m from the pin</>
            )}
            {' · '}
            {c.verdict === 'No site pinned' ? (
              <>
                this job has no pinned location, so the reading cannot be placed
                {' — '}
                <Link to="/office/job-locations">pin it</Link>
              </>
            ) : (
              c.verdict
            )}
          </div>
        ))}

        {checkIns.length === 0 && detail.no_check_in_reason && (
          <div className="alert alert-warn" style={{ fontSize: '0.8125rem' }}>
            No location recorded — <strong>{detail.no_check_in_reason}</strong>
          </div>
        )}

        {posted && (
          <div className="alert alert-warn">
            This crew day has posted to Sage. Corrections must be made in Sage —
            the portal will not edit posted time.
          </div>
        )}
      </div>

      {/* Shifts and the task narrative. Shown only when something was actually
          recorded — both are optional and most crew days will have neither.
          None of this reaches Sage: PR_TimeTrack has no free-text column, and
          the times here are descriptive rather than the basis for pay. */}
      {(detail.work_performed || detail.people.length > 0) && (
        <div className="card">
          <h2>Shift and work performed</h2>
          {detail.work_performed && (
            <p style={{ marginTop: 0 }}>{detail.work_performed}</p>
          )}
          {detail.people.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style={{ textAlign: 'right' }}>Span</th>
                    <th>Work performed</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.people.map((p) => (
                    <tr key={p.sage_employee_key}>
                      <td>{p.display_name ?? p.employee_no ?? p.sage_employee_key}</td>
                      <td className="mono">{clockLabel(p.start_time)}</td>
                      <td className="mono">{clockLabel(p.end_time)}</td>
                      {/* Muted because it is not a payable number. Hours are
                          in the Lines table below and are entered, not
                          derived — the two legitimately differ. */}
                      <td className="mono muted" style={{ textAlign: 'right' }}>
                        {p.span_hours ? Number(p.span_hours).toFixed(2) : '—'}
                      </td>
                      <td>{p.work_performed ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!posted && (
        <div className="card">
          <h2>Reason for corrections</h2>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            {isField ? (
              <>
                <strong>Required</strong> for every change — even just “typo”.
                Recorded against each change made while it is filled in.
              </>
            ) : (
              <>
                Optional, but recorded against every change made while it is
                filled in. Worth a line when someone queries a paycheque months
                from now.
              </>
            )}
          </p>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. foreman reported wrong hours for Tuesday"
            maxLength={300}
          />
        </div>
      )}

      {!posted && (
        <div className="card">
          <div className="crew-heading">
            <h2 style={{ margin: 0 }}>Wrong job or date?</h2>
            {!moveOpen && (
              <button className="btn btn-secondary" onClick={openMove}>
                Move this day
              </button>
            )}
          </div>
          {moveOpen && (
            <div className="stack" style={{ marginTop: '0.75rem' }}>
              <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
                Moves every line, the per diem and the site reading together.
                Per diem is re-priced for the new job. Uses the reason above.
              </p>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label htmlFor="move-job">Job</label>
                <select
                  id="move-job"
                  value={moveJob}
                  onChange={(e) => {
                    setMoveJob(e.target.value)
                    setPreview(null)
                  }}
                >
                  <option value={detail.job_no}>
                    {detail.job_no} {detail.job_description ?? ''} (current)
                  </option>
                  {jobs
                    .filter((j) => j.job_no !== detail.job_no)
                    .map((j) => (
                      <option key={j.job_no} value={j.job_no}>
                        {j.job_no} {j.job_description ?? ''}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0, maxWidth: 220 }}>
                <label htmlFor="move-date">Date</label>
                <input
                  id="move-date"
                  type="date"
                  value={moveDate}
                  onChange={(e) => {
                    setMoveDate(e.target.value)
                    setPreview(null)
                  }}
                />
              </div>

              {preview && (
                <>
                  {preview.needs_review && (
                    <div className="alert alert-warn">{preview.needs_review}</div>
                  )}
                  {preview.warnings.map((w) => (
                    <div key={w} className="alert alert-warn">
                      {w}
                    </div>
                  ))}
                  {!preview.needs_review && preview.warnings.length === 0 && (
                    <div className="alert alert-ok">Nothing to flag. Ready to move.</div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {preview ? (
                  <button className="btn" disabled={busy} onClick={() => void runMove(false)}>
                    {busy ? 'Moving…' : `Move to ${moveJob} on ${moveDate}`}
                  </button>
                ) : (
                  <button
                    className="btn"
                    disabled={
                      busy || (moveJob === detail.job_no && moveDate === detail.date_worked)
                    }
                    onClick={() => void runMove(true)}
                  >
                    {busy ? 'Checking…' : 'Check the move'}
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setMoveOpen(false)
                    setPreview(null)
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2>Lines</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Crew member</th>
                <th>Work type</th>
                <th>Codes sent to Sage</th>
                <th className="num">Hours / amount</th>
                <th>Sage</th>
                {!posted && <th></th>}
              </tr>
            </thead>
            <tbody>
              {detail.entries.map((e) => {
                const isEditing = editing === e.time_entry_id
                const locked = e.post_status === 'Posted' || e.post_status === 'Exported'
                const fixedAmount = isField && e.cost_code === PER_DIEM_COST_CODE
                return (
                  <tr key={e.time_entry_id}>
                    <td>
                      {e.display_name}{' '}
                      <span className="mono muted" style={{ fontSize: '0.75rem' }}>
                        {e.employee_no}
                      </span>
                    </td>
                    <td>
                      {isEditing ? (
                        <select
                          value={draft.workTypeId}
                          onChange={(ev) =>
                            setDraft({ ...draft, workTypeId: Number(ev.target.value) })
                          }
                          aria-label="Work type"
                        >
                          {draft.workTypeId === 0 && (
                            <option value={0}>— unmatched —</option>
                          )}
                          {workTypes
                            // Nobody in the field may turn a line into a typed
                            // per diem amount.
                            .filter((w) => !(isField && w.cost_code === PER_DIEM_COST_CODE))
                            .map((w) => (
                              <option key={w.work_type_id} value={w.work_type_id}>
                                {w.label}
                              </option>
                            ))}
                        </select>
                      ) : (
                        workTypes.find(
                          (w) =>
                            w.cost_code === e.cost_code &&
                            w.earnings_code === e.earnings_code,
                        )?.label ?? (e.earnings_description ?? e.earnings_code)
                      )}
                      {/* Travel booked to the job they drove TO. Shown on the
                          line rather than in the header, because the header
                          job is still right for everything else on the day. */}
                      {e.override_job_no && (
                        <div className="muted" style={{ fontSize: '0.75rem' }}>
                          ↳ charged to {e.override_job_no}
                          {e.override_job_description
                            ? ` · ${e.override_job_description}`
                            : ''}
                        </div>
                      )}
                    </td>
                    {/* Shown read-only so accounting can see exactly what will
                        reach Sage. They change together via the work type. */}
                    <td className="mono" style={{ fontSize: '0.8125rem' }}>
                      {e.cost_code ?? '—'}
                      <div className="muted">{e.earnings_code}</div>
                    </td>
                    <td className="num">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          value={draft.value}
                          onChange={(ev) => setDraft({ ...draft, value: ev.target.value })}
                          style={{ width: 90 }}
                          autoFocus
                        />
                      ) : e.entry_type === 'Hours' ? (
                        Number(e.hours_worked).toFixed(2)
                      ) : (
                        `$${Number(e.amount).toFixed(2)}`
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          e.post_status === 'Posted'
                            ? 'badge-ok'
                            : e.post_status === 'Failed'
                              ? 'badge-error'
                              : 'badge-info'
                        }`}
                      >
                        {e.post_status}
                      </span>
                    </td>
                    {!posted && (
                      <td>
                        {locked ? (
                          <span className="muted" style={{ fontSize: '0.75rem' }}>
                            locked
                          </span>
                        ) : isEditing ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn"
                              style={{ minHeight: 32 }}
                              disabled={busy}
                              onClick={() => void save(e)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 32 }}
                              onClick={() => setEditing(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            {!fixedAmount && (
                              <button
                                className="btn btn-secondary"
                                style={{ minHeight: 32 }}
                                onClick={() => startEdit(e)}
                              >
                                Correct
                              </button>
                            )}
                            <button
                              className="btn btn-secondary"
                              style={{ minHeight: 32 }}
                              disabled={busy}
                              onClick={() => void remove(e)}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {audit.length > 0 && (
        <div className="card">
          <div className="crew-heading">
            <h2 style={{ margin: 0 }}>Correction history</h2>
            <span className="count-pill count-pill-empty">{audit.length}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Crew member</th>
                  {/* Tim, 2 Nov: two lines for one person changed together
                      (hours and per diem) read as a duplicate without it. */}
                  <th>Work type</th>
                  <th>Change</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.audit_id}>
                    <td>{new Date(a.action_utc + 'Z').toLocaleString()}</td>
                    <td>{a.actor_name ?? '—'}</td>
                    <td>{a.employee_name ?? '—'}</td>
                    <td>{a.work_type_label ?? '—'}</td>
                    <td>
                      <ChangeCell a={a} />
                    </td>
                    <td className="muted">{a.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
